import { apiFetch } from "./api.js";
import { lsGet, lsSet } from "../utils/storage.js";

const MIGRATION_FLAG_KEY = "qm_migrated_v1";

/**
 * Returns true if a migration has already been completed for this phone.
 * @param {string} phone
 */
function isMigrated(phone) {
  const flags = lsGet(MIGRATION_FLAG_KEY, {});
  return !!flags[phone];
}

/**
 * Mark migration as complete for this phone so it is never run again.
 * @param {string} phone
 */
function markMigrated(phone) {
  const flags = lsGet(MIGRATION_FLAG_KEY, {});
  flags[phone] = true;
  lsSet(MIGRATION_FLAG_KEY, flags);
}

/**
 * One-shot localStorage → backend migration.
 *
 * Migrates:
 *  - Chat history   (qm_chats[phone])
 *  - XP balance     (qm_xp[phone])
 *
 * RWD-7/RWD-8: the legacy `qm_civics` payload is no longer forwarded —
 * the civics table was dropped and historical CP has already been
 * collapsed into xp.total_earned by migration 033. Stale localStorage
 * civics data is left untouched on the client (harmless) but never sent.
 *
 * Silently no-ops if:
 *  - Already migrated for this phone
 *  - No data exists in localStorage
 *  - API is unavailable (errors are swallowed to avoid blocking login)
 *
 * @param {string} phone
 * @returns {Promise<{ skipped?: boolean; ok?: boolean }>}
 */
export async function migrateLocalStorageToDb(phone) {
  if (!phone) return { skipped: true };
  if (isMigrated(phone)) return { skipped: true };

  // Gather localStorage data for this phone
  const storedChats   = lsGet("qm_chats",  {});
  const storedXp      = lsGet("qm_xp",     {});

  const chats   = storedChats[phone]   ?? {};
  const xp      = storedXp[phone]      ?? 0;

  // Nothing to migrate
  const hasChats  = Object.keys(chats).length > 0;
  if (!hasChats && xp === 0) {
    markMigrated(phone);
    return { skipped: true };
  }

  try {
    const result = await apiFetch(
      `/api/voters/${encodeURIComponent(phone)}/migrate`,
      {
        method: "POST",
        body: JSON.stringify({ xp, chats }),
      }
    );
    markMigrated(phone);
    return result;
  } catch {
    // Non-blocking: migration failure should never prevent login
    return { skipped: true };
  }
}
